import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface TopUpPackDraft {
  id: string;
  label: string;
  credits: number;
  price: number;
  currency: string;
  isActive: boolean;
  popular: boolean;
  sortOrder: number;
}

const newPackForm = (sortOrder = 10): TopUpPackDraft => ({
  id: '',
  label: '',
  credits: 10,
  price: 99,
  currency: 'INR',
  isActive: true,
  popular: false,
  sortOrder,
});

const normalizePack = (pack: any, index = 0): TopUpPackDraft => ({
  id: String(pack.id || ''),
  label: String(pack.label || ''),
  credits: Number(pack.credits || 0),
  price: Number(pack.price || 0),
  currency: String(pack.currency || 'INR'),
  isActive: pack.isActive !== false,
  popular: Boolean(pack.popular),
  sortOrder: Number(pack.sortOrder ?? index + 1),
});

const AdminTopUps: React.FC = () => {
  const { user } = useAuth();
  const [packs, setPacks] = useState<TopUpPackDraft[]>([]);
  const [form, setForm] = useState<TopUpPackDraft>(() => newPackForm());
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TopUpPackDraft | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const isAdmin = user?.role === 'admin';

  const nextSortOrder = useMemo(() => {
    if (!packs.length) return 10;
    return Math.max(...packs.map((pack) => Number(pack.sortOrder || 0))) + 10;
  }, [packs]);

  const loadPacks = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/top-up-packs');
      setPacks((response.data.packs || []).map(normalizePack));
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Unable to load top-up packs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    loadPacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, sortOrder: nextSortOrder }));
  }, [nextSortOrder]);

  const updatePack = (id: string, patch: Partial<TopUpPackDraft>) => {
    setPacks((prev) => prev.map((pack) => (pack.id === id ? { ...pack, ...patch } : pack)));
  };

  const validatePack = (pack: TopUpPackDraft) => {
    if (!pack.label.trim()) return 'Enter a top-up name.';
    if (!Number.isFinite(Number(pack.credits)) || Number(pack.credits) <= 0) return 'Unlock ride count must be greater than 0.';
    if (!Number.isFinite(Number(pack.price)) || Number(pack.price) <= 0) return 'Price must be greater than 0.';
    return '';
  };

  const createPack = async () => {
    const validation = validatePack(form);
    if (validation) {
      setMessage(validation);
      return;
    }

    setCreating(true);
    try {
      const response = await api.post('/api/admin/top-up-packs', form);
      setPacks((response.data.packs || []).map(normalizePack));
      setForm(newPackForm(nextSortOrder + 10));
      setMessage('Top-up pack created.');
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Unable to create top-up pack.');
    } finally {
      setCreating(false);
    }
  };

  const savePack = async (pack: TopUpPackDraft) => {
    const validation = validatePack(pack);
    if (validation) {
      setMessage(validation);
      return;
    }

    setSavingId(pack.id);
    try {
      const response = await api.patch(`/api/admin/top-up-packs/${pack.id}`, pack);
      setPacks((response.data.packs || []).map(normalizePack));
      setMessage('Top-up pack saved.');
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Unable to save top-up pack.');
    } finally {
      setSavingId(null);
    }
  };

  const deletePack = async () => {
    if (!deleteTarget) return;
    setSavingId(deleteTarget.id);
    try {
      const response = await api.delete(`/api/admin/top-up-packs/${deleteTarget.id}`);
      setPacks((response.data.packs || []).map(normalizePack));
      setMessage('Top-up pack deleted.');
      setDeleteTarget(null);
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Unable to delete top-up pack.');
    } finally {
      setSavingId(null);
    }
  };

  if (!isAdmin) {
    return (
      <Stack spacing={2} sx={{ maxWidth: 960, mx: 'auto' }}>
        <Alert severity="error">Admin access is required to manage top-up packs.</Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1120, mx: 'auto' }}>
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight={900}>Top-up Packs</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage wallet recharge packs separately from subscription plans. These values appear in web wallet, mobile wallet, and checkout.
            </Typography>
          </Box>
          <Chip label="Admin" color="primary" variant="outlined" sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }} />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={900}>Create Top-up Pack</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          A top-up pack adds unlock credits to the driver's wallet after payment.
        </Typography>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Pack name"
              value={form.label}
              onChange={(event) => setForm((prev) => ({ ...prev, label: event.target.value }))}
              placeholder="e.g. 25 extra unlocks"
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Unlock ride count"
              value={form.credits}
              inputProps={{ min: 1 }}
              onChange={(event) => setForm((prev) => ({ ...prev, credits: Number(event.target.value) }))}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Price"
              value={form.price}
              inputProps={{ min: 1, step: 1 }}
              onChange={(event) => setForm((prev) => ({ ...prev, price: Number(event.target.value) }))}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              fullWidth
              type="number"
              label="Order"
              value={form.sortOrder}
              inputProps={{ min: 0 }}
              onChange={(event) => setForm((prev) => ({ ...prev, sortOrder: Number(event.target.value) }))}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2.5}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap">
              <FormControlLabel
                control={<Switch checked={form.isActive} onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))} />}
                label="Active"
              />
              <FormControlLabel
                control={<Switch checked={form.popular} onChange={(event) => setForm((prev) => ({ ...prev, popular: event.target.checked }))} />}
                label="Popular"
              />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Button startIcon={<AddIcon />} variant="contained" onClick={createPack} disabled={creating}>
              {creating ? 'Creating...' : 'Create top-up pack'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1.5} mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={900}>Existing Top-up Packs</Typography>
            <Typography variant="body2" color="text.secondary">
              Disable a pack to hide it from buyers, or delete it if it should no longer be offered.
            </Typography>
          </Box>
          <Button variant="outlined" onClick={loadPacks} disabled={loading}>Refresh</Button>
        </Stack>

        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress size={28} />
          </Box>
        ) : packs.length === 0 ? (
          <Alert severity="info">No top-up packs configured. Create one above to show recharge options in wallet.</Alert>
        ) : (
          <Stack spacing={1.5}>
            {packs.map((pack) => (
              <Box
                key={pack.id}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: pack.isActive ? 'divider' : 'action.disabledBackground',
                  borderRadius: 2,
                  bgcolor: pack.isActive ? 'background.paper' : 'action.hover',
                }}
              >
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} md={3.5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Pack name"
                      value={pack.label}
                      onChange={(event) => updatePack(pack.id, { label: event.target.value })}
                    />
                    <Typography variant="caption" color="text.secondary">ID: {pack.id}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3} md={1.75}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Unlocks"
                      value={pack.credits}
                      inputProps={{ min: 1 }}
                      onChange={(event) => updatePack(pack.id, { credits: Number(event.target.value) })}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={1.75}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Price"
                      value={pack.price}
                      inputProps={{ min: 1, step: 1 }}
                      onChange={(event) => updatePack(pack.id, { price: Number(event.target.value) })}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={1.25}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Order"
                      value={pack.sortOrder}
                      inputProps={{ min: 0 }}
                      onChange={(event) => updatePack(pack.id, { sortOrder: Number(event.target.value) })}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={1.5}>
                    <FormControlLabel
                      control={<Switch checked={pack.isActive} onChange={(event) => updatePack(pack.id, { isActive: event.target.checked })} />}
                      label="Active"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={1.5}>
                    <FormControlLabel
                      control={<Switch checked={pack.popular} onChange={(event) => updatePack(pack.id, { popular: event.target.checked })} />}
                      label="Popular"
                    />
                  </Grid>
                  <Grid item xs={12} md={0.75}>
                    <Stack direction={{ xs: 'row', md: 'column' }} spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Save changes">
                        <span>
                          <IconButton color="primary" onClick={() => savePack(pack)} disabled={savingId === pack.id}>
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeleteTarget(pack)}
                        disabled={savingId === pack.id}
                        sx={{ justifyContent: 'flex-start' }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Delete top-up pack?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This removes
            {' '}
            <Box component="span" color="text.primary" fontWeight={800}>
              {deleteTarget?.label || 'this pack'}
            </Box>
            {' '}
            from future wallet recharge options. Existing payment history will remain unchanged.
          </Typography>
          <Divider sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={Boolean(savingId)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deletePack} disabled={Boolean(savingId)}>
            Delete pack
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(message)} autoHideDuration={3500} onClose={() => setMessage(null)} message={message} />
    </Stack>
  );
};

export default AdminTopUps;
